// Sale campaigns (spec/dashboard.md section 7.6): cards per LiveOps.Store.SaleCampaign with the multiplier, the store items,
// the optional event and the condition with its time status.
import type { ReactNode } from 'react';
import type { Json } from '../../api/types';
import { CardList } from '../../content/cards/CardList';
import { ConditionSummary } from '../../content/conditions/ConditionBuilder';
import { Filter, Percent } from '../../ui/icons';
import { BodyField, Chips, CommonTail, NameText, paramsOf } from './cards';
import './liveops.css';

export const SALE = 'LiveOps.Store.SaleCampaign';

/** A titled group of the expanded body; `single` hides the lone field's label visually. */
function Group({ title, single, children }: { title: string; single?: boolean; children: ReactNode }) {
  return (
    <section className={`lo-group ${single ? 'single' : ''}`} aria-label={title}>
      <h4 className="lo-group-title">{title}</h4>
      {children}
    </section>
  );
}

export function SalesPage() {
  return (
    <CardList
      id="sales"
      title="Sales"
      template={SALE}
      includeHeirs={false}
      header={(ctx) => {
        const params = paramsOf(SALE);
        const d = (ctx.doc.data ?? {}) as Record<string, Json>;
        return (
          <div className="card-head-lines">
            <div className="lo-head-line">
              <NameText doc={ctx.doc} />
              <span className="lo-cond">
                <Filter aria-hidden="true" />
                <ConditionSummary rootId={typeof d.condition === 'string' ? d.condition : null} />
              </span>
            </div>
            <Chips ctx={ctx} params={params} names={['multiplier', 'priority']} />
          </div>
        );
      }}
      body={(ctx) => {
        const params = paramsOf(SALE);
        return (
          <div className="card-fields lo-body solo">
            <div className="card-fields-main">
              <Group title="Sale">
                <BodyField ctx={ctx} params={params} name="name" />
              </Group>
              <Group title="Store items and event">
                <BodyField ctx={ctx} params={params} name="storeItems" help="Empty = the store's Sales list (multipliedStoreItems)" />
                <BodyField ctx={ctx} params={params} name="gameEvent" help="When set, the sale is active only while this event runs" />
              </Group>
              <Group title="Condition" single>
                <BodyField ctx={ctx} params={params} name="condition" />
              </Group>
              <Group title="Notes">
                <CommonTail ctx={ctx} />
              </Group>
            </div>
          </div>
        );
      }}
      filterFields={[{ key: 'data.multiplier', label: 'Multiplier', type: 'number', group: 'Sale' }]}
      emptyText="No sale campaigns yet. A sale applies its multiplier to store items while its condition (and its event, when set) holds."
      emptyIcon={<Percent aria-hidden="true" />}
    />
  );
}
